import React, { useState, useEffect, useRef } from "react";
import * as Video from "twilio-video";
import { FaMicrophone, FaMicrophoneSlash, FaVideo, FaVideoSlash, FaVolumeMute, FaVolumeUp, FaPaperPlane, FaComment, FaPhone, FaStar, FaEye, FaPauseCircle } from "react-icons/fa";
import { FaChevronDown, FaChevronUp, FaFileLines, FaMoon, FaRegCircleDot, FaShare, FaUser } from "react-icons/fa6";
import { TbGridDots, TbSunFilled } from "react-icons/tb";
import { PiPhoneOutgoingFill } from "react-icons/pi";
import { useAppSelector, useAppDispatch } from "src/store/hooks";
import moment from "moment";

// Components
import SidePanel from "../components/panels/panel";
import Participant from "../components/video-participant";
import PanelStatus from "../components/panels/panel-status";
import PanelPrePause from "../components/panels/panel-pre-pause";
import PanelDialer from "../components/panels/panel-dialer";
import PanelTransference from "../components/panels/panel-transference";
import PanelUdaInfo from "../components/panels/panel-user";
import Timer from "../../components/timer";
import DeviceSelector from "../components/device-selector";
import Tooltip from "src/components/tooltip";
import ControlButton from "../components/control-button";

// Import needed
import { ws } from "src/services/ws";
import { Recorder } from "src/services/recorder";

// Actions
import { setTrackState, toggleSupervisor } from "src/store/call/reducer";
import { doUploadRecording } from "src/store/call/actions";
import { updateDevices } from "src/store/media/reducer";

// Helpers
import { sleep, useOutsideClick, useTyping } from "src/helpers";
import { MediaTypes } from "src/types/check-devices";
import { MediaManagerInstance } from "src/services/media-manager";

// Types
import { TabType } from "src/types";

// Reducers
import { setGeneral } from "src/store/config/reducer";
import { toast } from "react-toastify";
import PSTNCall from "src/models/pstn-call";

const DeviceDropup = ({ type, selectCallback, children }: { type: MediaTypes; selectCallback?: Function; children: React.ReactNode }) => {
  const innerRef = useRef(null);
  const dispatch = useAppDispatch();
  const { inputDeviceId, cameraDeviceId, outputDeviceId, availableDevices } = useAppSelector((state) => state.media);

  // States
  const [status, setStatus] = useState<boolean>(false);
  const [deviceList, setDevices] = useState<Array<MediaDeviceInfo>>([]);

  const toggleList = async () => {
    if (!status) {
      await MediaManagerInstance.checkDevices();
      setDevices(availableDevices.filter((d) => d.kind === type));
    }

    return setStatus(!status);
  };

  useOutsideClick(innerRef, () => setStatus(false));

  const selectDeviceHandler = (device: MediaDeviceInfo) => {
    dispatch(updateDevices({ type, deviceId: device.deviceId }));

    return selectCallback && selectCallback(device);
  };

  let icon;
  let deviceId;

  switch (type) {
    case MediaTypes.AUDIO:
      icon = FaVolumeUp;
      deviceId = outputDeviceId;
      break;

    case MediaTypes.CAM:
      icon = FaVideo;

      const found = deviceList.find((d) => d.deviceId === cameraDeviceId);
      if (!found) {
        deviceId = deviceList[0]?.deviceId;
      } else deviceId = cameraDeviceId;
      break;

    case MediaTypes.MIC:
      icon = FaMicrophone;
      deviceId = inputDeviceId;
      break;

    default:
      break;
  }

  // Effect
  useEffect(() => {
    setDevices(availableDevices.filter((d) => d.kind === type));
  }, [availableDevices]);

  return (
    <div ref={innerRef} className="device-group relative bg-[#cbcfd6] dark:bg-[#3a3c56] rounded-lg flex z-10">
      <button type="button" onClick={toggleList} className="w-11 h-12 flex justify-center items-center text-white">
        {status ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
      </button>

      {status && (
        <div className="device-group-dropup absolute bottom-full bg-[#20212e] p-2 rounded-3xl max-w-[250px]">
          <DeviceSelector options={deviceList} Icon={icon} defaultDeviceId={deviceId} selectDeviceCallback={selectDeviceHandler} />
        </div>
      )}

      {children}
    </div>
  );
};

export default function VideoChat({
  // Methods
  endCall,
  startCallBack,
  addMessage,
  setFinishStatus,

  // Variables
  innerRef,
  twilioRoomName,
  twilioToken,
  callEnding,

  // Status flags
  udaIsTyping,
}: {
  // Methods
  endCall: Function;
  startCallBack?: Function;
  addMessage: Function;
  setFinishStatus: Function;

  // Variables
  innerRef: any;
  twilioRoomName: string;
  twilioToken: string;
  callEnding: boolean;

  // Status flags
  udaIsTyping: boolean;
}) {
  // Get pre call props
  const { userInfo, accessToken } = useAppSelector((state) => state.auth);
  const { callDetails, udaQueueId } = useAppSelector((state) => state.call);
  const { localMediaId } = useAppSelector((state) => state.media);
  const general = useAppSelector((state) => state.config.general);

  const recorderHookRef = useRef<string | null>(null);

  // CARROSSEL
  const activeNumber = callDetails?.pstnNumbers.find((pstn) => pstn.active);
  const pstnLefover: PSTNCall[] = activeNumber && callDetails?.pstnNumbers.length ? callDetails?.pstnNumbers.filter((n) => n.internalId !== activeNumber.internalId && !n.called) : [];

  // Create a dispatcher
  const dispatch = useAppDispatch();

  // Participants
  const [participants, setParticipants] = useState<Array<Video.RemoteParticipant>>([]);

  // Room & Error
  const [room, setRoom] = useState<Video.Room | null>(null);
  const [error, setError] = useState<Video.TwilioError | null>(null);

  const wsBinds: Array<number> = [];

  // Main State
  const [state, setState] = useState<{
    internalMessages: Array<any>;
    udaVideoMuted: boolean;
    udaWasConnected: boolean;
    unreadMessages: number;
    messageInput: string;
    tabOpened: TabType | null;
    dialerOpened: boolean;

    isRecording: boolean;
    canRecord: boolean;

    selectedFinishStatus: string | null;
    localTracks: Array<MediaStreamTrack> | null;
    darkMode: boolean;
    udaTracksPublished: boolean;
    supervisorVideoHidden: boolean;
    supervisorFirstAttempt: boolean;
  }>({
    // => Other status
    tabOpened: TabType.SCRIPT,
    dialerOpened: false,
    isRecording: callDetails!.servico.gravar_ligacoes,
    canRecord: callDetails!.servico.gravar_ligacoes,
    selectedFinishStatus: null,
    darkMode: general.theme === "dark",

    // => Uda Status
    udaVideoMuted: false,
    udaWasConnected: false,
    udaTracksPublished: true,

    // => Supervisor
    supervisorVideoHidden: true,
    supervisorFirstAttempt: false,

    // => Webchat
    internalMessages: [],
    unreadMessages: 0,
    messageInput: "",

    // Video recorder
    // recorder: null,
    localTracks: null,
  });

  const disableSuperVisorRequest = () => {
    ws.sendMessage({
      type: "PA_NEEDS_NO_SUPERVISOR_HELP",
    });

    dispatch(toggleSupervisor(false));
  };

  // Hook typing
  useTyping(state.messageInput, () =>
    ws.sendMessage({
      type: "PA_IS_TYPING",
      data: {
        udaQueueId: udaQueueId,
      },
    }),
  );

  // Theme dispatcher
  const themeToggler = () => {
    dispatch(setGeneral({ ...general, theme: state.darkMode ? "light" : "dark" }));
  };

  // Connection
  useEffect(() => {
    let id = 1;

    console.log("Video-Chat: Componente de vídeo iniciado.");

    // Recorder
    recorderHookRef.current = Recorder.addOnDataListner(async (blob: Blob) => {
      const token = accessToken!.replace("Bearer ", "");
      const formData = new FormData();
      formData.append("file", blob, "video-part.webm");

      const { success } = await doUploadRecording(callDetails!.id, token, callDetails!.aderenteSlug, callDetails!.uda.id, callDetails!.servico.id, formData, "upload", { id });

      id++;
      // if (success) console.log("Video-Chat: Blob upload", success);
    });

    const cleanUp = async () => {
      // Dispose hook
      recorderHookRef.current && Recorder.removeDataListner(recorderHookRef.current);

      setRoom((currentRoom) => {
        if (currentRoom && currentRoom.localParticipant.state === "connected") {
          currentRoom.disconnect();
          return null;
        } else {
          return currentRoom;
        }
      });
    };

    const participantConnected = async (participant: any) => {
      setParticipants((prevParticipants) => [...prevParticipants, participant]);

      // Execute only when UDA connected first time
      if (participant.identity.indexOf("uda") > -1 && !state.udaWasConnected) {
        setState((s) => ({ ...s, udaWasConnected: true }));

        // Define Call as Started
        if (startCallBack) {
          await startCallBack();
        }
      }

      if (participant.identity == "supervisor") {
        disableSuperVisorRequest();
        setState((s) => ({ ...s, supervisorFirstAttempt: true }));
      }
    };

    const participantDisconnected = async (participant: any) => {
      setParticipants((prevParticipants) => prevParticipants.filter((p) => p !== participant));

      if (participant.identity.indexOf("uda") > -1) {
        await finishCallConference(false);
      } else if (participant.identity === "supervisor") {
        setState((s) => ({ ...s, supervisorFirstAttempt: false }));
      }
    };

    async function startTwilioConference() {
      // Init conference
      console.log("Client: TWILIO INITED");

      const localMediaStream = MediaManagerInstance.getMediaStream([MediaTypes.CAM, MediaTypes.MIC]);

      if (localMediaStream) setState((s) => ({ ...s, localTracks: localMediaStream?.getTracks() }));

      try {
        const _room = await Video.connect(twilioToken!, {
          name: twilioRoomName,
          region: "br1",
          tracks: localMediaStream?.getTracks(),
          maxAudioBitrate: 16000,
          preferredVideoCodecs: [{ codec: "VP8", simulcast: false }],
        });

        // Timeout
        setTimeout(async () => {
          if (_room.participants.size === 0) {
            toast.warning("UDA não conectou a sala. Finalizando chamada...");
            await endCall(true);
          }
        }, 10000);

        // Save room
        setRoom(_room);
        innerRef.current = room;

        // Map current participants
        _room.participants.forEach(participantConnected);

        // Bind some events
        _room.on("participantConnected", participantConnected);
        _room.on("participantDisconnected", participantDisconnected);

        _room.on("trackSubscribed", async (track, publication, participant) => {
          if (!["audio", "video"].includes(track.kind)) return;

          // Check if UDA
          if (participant.identity.indexOf("uda") > -1) {
            console.log(Recorder?.getStatus());

            // Se as tracks não estiverem publicadas então reseta.
            if (track.kind == "video") {
              // if (!udaTracksPublished && track.kind == "video") {
              const rTrack = track.mediaStreamTrack;

              if (callDetails!.servico.gravar_ligacoes) {
                const lStream = localMediaStream!;
                const rStream = new MediaStream([rTrack!]);
                const recorderStatus = await Recorder.getStatus();

                if (recorderStatus === "stopped" || recorderStatus === undefined) {
                  Recorder.addMediaStreams([lStream, rStream]);
                  Recorder.startRecording();
                } else {
                  Recorder.setVideoStreams([lStream, rStream]);
                }
              }

              // Set status to published
            } else {
              // Adiciona Audio do UDA
              const mediaStreamTrack = track as Video.RemoteAudioTrack | Video.RemoteVideoTrack;

              const mediaStream = new MediaStream([mediaStreamTrack.mediaStreamTrack]);

              Recorder.addMediaStreams([mediaStream]);
            }

            setState((s) => ({ ...s, udaTracksPublished: true }));
          }
        });

        _room.on("trackUnsubscribed", (track, publication, participant) => {
          if (participant.identity.indexOf("uda") > -1)
            // Set unpulished general status
            setState((s) => ({ ...s, udaTracksPublished: false }));
          else if (participant.identity == "supervisor") setState((s) => ({ ...s, supervisorVideoHidden: true }));
        });

        _room.on("trackDisabled", (track, participant) => {
          if (track.kind === "video") {
            if (participant.identity.indexOf("uda") > -1) {
              setState((s) => ({ ...s, udaVideoMuted: true }));
            } else if (participant.identity === "supervisor") {
              setState((s) => ({ ...s, supervisorVideoHidden: true }));
            }
          }
        });

        _room.on("trackEnabled", (track, participant) => {
          if (track.kind === "video") {
            if (participant.identity.indexOf("uda") > -1) {
              setState((s) => ({ ...s, udaVideoMuted: false }));
            } else if (participant.identity === "supervisor") {
              setState((s) => ({
                ...s,
                supervisorVideoHidden: false,
                supervisorFirstAttempt: false,
              }));
            }
          }
        });
      } catch (err) {
        const _err = err as Video.TwilioError;
        setError(_err);
      }
    }

    if (twilioRoomName && twilioToken) startTwilioConference();

    wsBinds.push(ws.on("UDA_ENDED_CALL", () => finishCallConference(false)));

    return () => {
      ws.clearBinds(wsBinds);
      cleanUp();
    };
  }, [twilioRoomName, twilioToken]);

  // Detect change devices
  useEffect(() => {
    if (state.localTracks && room) {
      const { localParticipant } = room;

      // Get media stream
      const localMediaStream = MediaManagerInstance.getMediaStream([MediaTypes.CAM, MediaTypes.MIC]);

      // Map tracks
      const localMediaTracks = localMediaStream?.getTracks() || [];

      // Map trackIds
      const newIds = localMediaTracks.map((t) => t.id);

      // Map current trackIds
      const oldIds = state.localTracks.map((t) => t.id);

      // Map only changed tracks to unpub
      const tracksToUnpub = state.localTracks.filter((t) => !newIds.includes(t.id));
      const tracksToPub = localMediaTracks.filter((t) => !oldIds.includes(t.id));

      // If local tracks
      if (localMediaTracks) {
        localParticipant.unpublishTracks(tracksToUnpub);
        tracksToUnpub.forEach((t) => t.stop());

        // Overwrite with new tracks
        setState((s) => ({ ...s, localTracks: localMediaTracks }));
        localParticipant.publishTracks(tracksToPub);

        const hasVideoToPub = tracksToPub.find((t) => t.kind === "video");
        const hasAudioToPub = tracksToPub.find((t) => t.kind === "audio");

        // Overwrite in recorder
        if (hasVideoToPub) {
          const udaParticipant = [...room.participants.values()].find((k) => k.identity.indexOf("uda") > -1);

          const entry = udaParticipant?.videoTracks.entries().next().value;
          if (entry) {
            const [trackKey, trackPublication]: [string, Video.VideoTrackPublication] = entry;
            const videoTrack = trackPublication.track?.mediaStreamTrack;

            const rStream = new MediaStream([videoTrack!]);
            const lStream = new MediaStream([hasVideoToPub]);

            if (Recorder) Recorder.setVideoStreams([lStream, rStream]);
          }
        }

        if (hasAudioToPub) {
          const audioMediaStream = new MediaStream([hasAudioToPub]);
          Recorder.addMediaStreams([audioMediaStream]);
        }
      }
    }
  }, [localMediaId]);

  // => Controls
  const setUdaTrackState = (forceVideo?: boolean, forceAudio?: boolean, forceRecording?: boolean) => {
    const videoState = forceVideo != null ? forceVideo : callDetails?.isVideoEnabled;
    const audioState = forceAudio != null ? forceAudio : callDetails?.isMicEnabled;
    const isRecording = forceRecording != null ? forceRecording : state.isRecording;

    // Mute all for UDA
    ws.sendMessage({
      type: "PA_SET_UDA_TWILIO_TRACKS_STATE",
      data: {
        udaId: udaQueueId,
        videoState,
        audioState,
        isRecording,
      },
    });
  };

  useEffect(setUdaTrackState, [callDetails?.isMicEnabled, callDetails?.isVideoEnabled]);

  // Transference state
  useEffect(() => {
    // Is on transference
    if (callDetails?.transferenceStatus.inTransference) {
      if (!callDetails.transferenceStatus.isTransferenceOrigin) {
        setUdaTrackState(false, false);
      }
    }

    // Is'nt on transference
    else {
      if (!callDetails?.transferenceStatus.isTransferenceOrigin) {
        setUdaTrackState(true, true);
      }
    }
  }, [callDetails?.transferenceStatus]);

  // Auto Scroll Mmessages
  useEffect(() => {
    if (callDetails) {
      if (state.internalMessages.length < callDetails.messageList.length) {
        const newState: any = {};

        // Update
        newState.internalMessages = callDetails.messageList;

        // Set new messages
        if (state.tabOpened !== TabType.WEBCHAT) newState.unreadMessages = state.unreadMessages + 1;

        setState((s) => ({ ...s, ...newState }));
      }

      // Scroll
      let elm = document.querySelector(".webchat--message-list");
      if (elm) elm.scrollTop = elm.scrollHeight;
    }
  }, [callDetails?.messageList]);

  // => Internal Methods
  const toggleRecording = () => {
    const { isRecording } = state;
    if (Recorder) {
      if (isRecording) Recorder.pauseRecording();
      else Recorder.resumeRecording();

      setState((s) => ({ ...s, isRecording: !s.isRecording }));
    }
  };

  // Finish call
  const finishCallConference = async (paEnded = false, putCallback?: boolean) => {
    // Stop
    try {
      if (callDetails!.servico.gravar_ligacoes) {
        const token = accessToken!.replace("Bearer ", "");
        await Recorder.stopRecording(true);

        // Send last
        doUploadRecording(callDetails!.id, token, callDetails!.aderenteSlug, callDetails!.uda.id, callDetails!.servico.id, undefined, "end");
      }
    } finally {
      // End call
      endCall(paEnded, putCallback);
    }
  };

  // Add webchat message
  const _addMessage = () => {
    if (state.messageInput.length) addMessage(true, userInfo.name, state.messageInput);
    setState((s) => ({ ...s, messageInput: "" }));
  };

  // Toggle Panels
  const toggleTab = async (tabDesired: TabType | null, callback?: Function) => {
    setState((s) => ({
      ...s,
      tabOpened: s.tabOpened === tabDesired ? null : tabDesired,
    }));

    await sleep(200);

    callback && callback();
  };

  const toggleDialer = () => setState((s) => ({ ...s, dialerOpened: !s.dialerOpened }));

  const requestSupervisor = () => {
    ws.sendMessage({
      type: "PA_NEEDS_SUPERVISOR_HELP",
    });

    dispatch(toggleSupervisor(true));
  };

  // Map remotes
  const paParticipants = participants
    .filter((participant) => participant.identity.indexOf("pa") > -1)
    .map((participant) => <Participant key={participant.sid} participant={participant} type="pa" forceMuted={!callDetails?.isAudioEnabled} />);

  const udaParticipants = participants
    .filter((participant) => participant.identity.indexOf("uda") > -1)
    .map((participant) => <Participant key={participant.sid} participant={participant} type="uda" forceMuted={!callDetails?.isAudioEnabled} />);

  const supervisorParticipant = participants
    .filter((participant) => participant.identity === "supervisor")
    .map((participant) => <Participant key={participant.sid} participant={participant} type="supervisor" forceMuted={!callDetails?.isAudioEnabled} />);

  const voipDialerInstance = callDetails?.dialerInstances?.find((i) => i.instanceName === "VOIP");

  return (
    <div className={`${state.darkMode && "dark"}  grow flex flex-col`}>
      <div className="video-chat-room dark:bg-neutral-gray-800 grow flex flex-col bg-white">
        {/* VIDEO CHAT TOP (Video/Script) */}
        <div className="video-chat-room--top grow flex px-8 pt-8 gap-6">
          {/* VIDEOS CONTAINER */}
          <section
            className={`video-container grow relative h-full dark:bg-neutral-gray-700 bg-neutral-gray-100 rounded-lg overflow-hidden flex flex-col justify-center ${
              state.isRecording ? "border-4 border-feedback-negative-500" : ""
            }`}
          >
            {/* Self cam */}
            <div className="main-participants top-6 right-6 mt-1 ml-1 absolute w-[229px] z-10 rounded-lg flex flex-col gap-3 overflow-hidden min-h-[128px]">
              {room && room.localParticipant ? (
                <>
                  <Participant
                    key={room.localParticipant.sid}
                    participant={room.localParticipant}
                    type="local"
                    childs={!callDetails?.isVideoEnabled ? <div className="bg-primitive-blue-700 text-center text-white py-1">Vídeo Pausado</div> : null}
                  />

                  {paParticipants}

                  {!state.supervisorVideoHidden && supervisorParticipant}
                </>
              ) : null}
            </div>

            {/* Other videos */}
            <div className="remote-participants grow items-center justify-center h-0 flex flex-col">
              {/* Error */}
              {error && <span className="connect-error">Erro ao se conectar a sala.</span>}

              {/* Video muted */}

              {!state.udaTracksPublished && (
                <div className="pa-warning-video-paused absolute top-2/4 bg-white p-2 rounded-md">
                  <div className="video-warn-box flex items-center gap-2">
                    <FaVideoSlash size={24} className="warn-icon" />
                    <div className="warn-text leading-none">
                      Aguarde...
                      <br />
                      <small>O(a) usuário tirou o foco do aplicativo.</small>
                    </div>
                  </div>
                </div>
              )}

              {state.udaVideoMuted && (
                <div className="pa-warning-video-paused absolute top-2/4 bg-white p-2 rounded-md">
                  <div className="video-warn-box flex items-center gap-2">
                    <FaVideoSlash size={24} className="warn-icon" />
                    <div className="warn-text leading-none">
                      Aguarde...
                      <br />
                      <small>O(a) usuário pausou o vídeo.</small>
                    </div>
                  </div>
                </div>
              )}

              {udaParticipants}

              {!state.udaWasConnected && (
                <div className="on-user-connecting text-base text-white font-bold flex flex-col gap-8 items-center">
                  <span className="loader spinner-1 mr-2 white"></span> O usuário está conectando
                </div>
              )}
            </div>

            {state.isRecording ? <div className="is-recording-label bg-feedback-negative-500 text-white font-bold py-3 px-4 rounded-tl-lg absolute right-0 bottom-0">Gravando</div> : null}

            {/* Transference panel */}
            {state.dialerOpened && <PanelDialer togglePanel={toggleDialer} dialer={voipDialerInstance} />}
          </section>

          {/* Script */}
          {state.tabOpened === TabType.SCRIPT && (
            <SidePanel panelName="Script" toggleAction={toggleTab}>
              <div className="h-full flex flex-col">
                <div
                  className="grow overflow-y-auto h-0 "
                  dangerouslySetInnerHTML={{
                    __html: callDetails!.servico.script_para_atendimento || "",
                  }}
                />
              </div>
            </SidePanel>
          )}

          {/* Sidebars */}
          {state.tabOpened === TabType.WEBCHAT && (
            <SidePanel panelName="Chat" toggleAction={toggleTab}>
              <div className="flex flex-col h-full">
                <ul className="webchat--message-list grow overflow-y-auto h-0 p-0 m-0 gap-6 flex flex-col">
                  {callDetails?.messageList.map((message) => (
                    <li key={Math.random()} className={`message-item-wrap block ${message.self ? "self" : ""}`}>
                      <div
                        className={`message-bag p-3 text-base mb-2 rounded-lg ${
                          message.self ? "bg-primary-blue-600 text-white rounded-ee-none" : "bg-secondary-blue text-black rounded-es-none"
                        }`}
                      >
                        {message.text}
                      </div>
                      <span className={`message-date block text-neutral-gray-900 w-fit ${message.self ? "ml-auto" : "mr-auto"}`}>
                        {moment(message.created_at).format("DD/MM/YYYY HH:mm")} ・ {message.self ? "(interprete)" : "(surdo)"}
                      </span>
                    </li>
                  ))}
                </ul>

                {udaIsTyping && <div className={`pa-typing w-3/4 p-3 text-base mb-2 rounded-lg bg-secondary-blue text-black mr-auto rounded-es-none`}>Digitando...</div>}

                <div className="webchat--commands flex gap-4">
                  <textarea
                    className="webchat--textarea grow border border-neutral-gray-200 py-3 px-4 rounded-lg"
                    placeholder="Digite aqui"
                    value={state.messageInput}
                    onChange={(e) => setState((s) => ({ ...s, messageInput: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        return e.key === "Enter" && _addMessage();
                      }
                    }}
                    rows={1}
                  />

                  <button className="webchat--send-btn bg-primary-blue-400 text-white rounded-lg w-12 h-12 items-center justify-center flex" type="button" onClick={_addMessage}>
                    <FaPaperPlane />
                  </button>
                </div>
              </div>
            </SidePanel>
          )}

          {/* Transference panel */}
          {state.tabOpened === TabType.TRANSFERENCE && <PanelTransference togglePanel={toggleTab} />}

          {/* Status Panel */}
          {state.tabOpened === TabType.STATUS && (
            <PanelStatus selectedStatus={callDetails!.finishStatus} togglePanel={toggleTab} statusList={callDetails!.finishStatuses} setStatus={setFinishStatus} />
          )}

          {state.tabOpened === TabType.PREPAUSE && <PanelPrePause togglePanel={toggleTab} />}

          {/* UDA */}
          {state.tabOpened === TabType.UDA && (
            <PanelUdaInfo aderenteSlug={callDetails?.aderenteSlug!} servicoId={callDetails?.servico.id!} togglePanel={toggleTab} udaInfo={callDetails?.uda} />
          )}
        </div>

        {/* VIDEO CHAT BOTTOM (Controls) */}
        <div className="video-chat-room--bottom p-8 flex">
          <div className="uda-infos flex gap-6 items-center dark:text-white grow">
            <ControlButton isActive={state.tabOpened === TabType.UDA} onClick={() => toggleTab(TabType.UDA)}>
              <FaUser />
            </ControlButton>

            <div className="flex flex-col">
              <span className="uda-name font-bold">{callDetails?.uda.usuario.nome}</span>
              <span className="call-timer text-sm">
                <Timer />
              </span>
            </div>
          </div>

          {/* Other controls */}
          <div className="center-controls flex justify-center grow gap-[18px]">
            {/* Audio */}
            <DeviceDropup type={MediaTypes.AUDIO} selectCallback={() => {}}>
              <ControlButton
                onClick={() =>
                  dispatch(
                    setTrackState({
                      isAudioEnabled: !callDetails?.isAudioEnabled,
                    }),
                  )
                }
              >
                {callDetails?.isAudioEnabled ? <FaVolumeUp size={22} /> : <FaVolumeMute size={22} />}
              </ControlButton>
            </DeviceDropup>

            {/* Cam */}
            <DeviceDropup type={MediaTypes.CAM} selectCallback={() => {}}>
              <ControlButton
                onClick={() => {
                  const action = !callDetails?.isVideoEnabled ? "enable" : "disable";

                  /* room?.localParticipant.videoTracks.forEach((publication) => {
                    publication.track[action]();
                  }); */

                  dispatch(
                    setTrackState({
                      isVideoEnabled: !callDetails?.isVideoEnabled,
                    }),
                  );
                }}
              >
                {callDetails?.isVideoEnabled ? <FaVideo size={22} /> : <FaVideoSlash size={22} />}
              </ControlButton>
            </DeviceDropup>

            {/* Mic */}
            <DeviceDropup type={MediaTypes.MIC} selectCallback={() => {}}>
              <ControlButton
                onClick={() =>
                  dispatch(
                    setTrackState({
                      isMicEnabled: !callDetails?.isMicEnabled,
                    }),
                  )
                }
              >
                {callDetails?.isMicEnabled ? <FaMicrophone size={22} /> : <FaMicrophoneSlash size={22} />}
              </ControlButton>
            </DeviceDropup>

            {/* Feature: Video Chat */}
            {callDetails?.features.includes("VIDEO_CHAT") && (
              <Tooltip show={udaIsTyping && state.tabOpened !== TabType.WEBCHAT} text={`${callDetails?.uda.usuario.nome} está digitando...`}>
                <ControlButton
                  id="webchat-btn"
                  isActive={state.tabOpened === TabType.WEBCHAT}
                  onClick={() =>
                    toggleTab(TabType.WEBCHAT, () => {
                      setState((s) => ({ ...s, unreadMessages: 0 }));
                    })
                  }
                >
                  <FaComment size={22} />

                  {state.unreadMessages > 0 && <span className="rounded-full w-3 h-3 box-content absolute top-[-7px] right-[-5px] bg-[#EB4136] border-[5px] border-neutral-gray-900"></span>}
                </ControlButton>
              </Tooltip>
            )}

            {/* Script */}
            <Tooltip text={`Script de atendimento`}>
              <ControlButton isActive={state.tabOpened === TabType.SCRIPT} onClick={() => toggleTab(TabType.SCRIPT)}>
                <FaFileLines size={22} />
              </ControlButton>
            </Tooltip>

            {/* Dialer */}
            <Tooltip text="Discador">
              <ControlButton onClick={() => toggleDialer()} isActive={state.dialerOpened}>
                <TbGridDots size={22} />
              </ControlButton>
            </Tooltip>

            {/* Transfer? */}
            <Tooltip text="Transferir chamada">
              <ControlButton isActive={state.tabOpened === TabType.TRANSFERENCE} onClick={() => toggleTab(TabType.TRANSFERENCE)}>
                <PiPhoneOutgoingFill size={22} />
              </ControlButton>
            </Tooltip>

            {/* Status Finalizador */}
            {callDetails?.finishStatuses && callDetails?.finishStatuses.length > 0 && (
              <Tooltip text="Status finalizador">
                <ControlButton isActive={state.tabOpened === TabType.STATUS} onClick={() => toggleTab(TabType.STATUS)}>
                  <FaStar size={22} />
                </ControlButton>
              </Tooltip>
            )}
          </div>

          <div className="right-controls flex justify-end grow gap-[18px]">
            {/* Dark/Light Mode */}
            <Tooltip text="Modo escuro/claro">
              <ControlButton
                onClick={() => {
                  setState((s) => ({ ...s, darkMode: !s.darkMode }));
                  themeToggler();
                }}
                isActive={state.darkMode}
              >
                {state.darkMode ? <FaMoon size={22} /> : <TbSunFilled size={22} />}
              </ControlButton>
            </Tooltip>

            {/* Recording Call Status */}
            <Tooltip text="Gravação da chamada">
              <ControlButton
                name="record-call"
                disabled={!callDetails!.servico.gravar_ligacoes}
                isActive={state.isRecording}
                activeClassName="dark:bg-white bg-neutral-gray-100 text-feedback-negative-500"
                onClick={toggleRecording}
              >
                <FaRegCircleDot size={22} />
              </ControlButton>
            </Tooltip>

            {/* Pre pause */}
            <Tooltip text="Pausa agendada">
              <ControlButton name="pre-pause" isActive={state.tabOpened === TabType.PREPAUSE} onClick={() => toggleTab(TabType.PREPAUSE)}>
                <FaPauseCircle size={22} />
              </ControlButton>
            </Tooltip>

            {/* ?? */}
            <Tooltip text="Solicitar supervisão">
              <ControlButton onClick={() => requestSupervisor()} disabled={callDetails?.waitingSupervisor} isActive={callDetails?.waitingSupervisor}>
                <FaEye size={22} />
              </ControlButton>
            </Tooltip>

            {/* Endcall */}
            {callDetails?.features.includes("CARROSSEL") && (pstnLefover.length || !activeNumber?.called) && !callDetails.transferenceStatus.isTransferenceOrigin ? (
              <ControlButton name="endCall" className="bg-[#FEB53C] text-white w-[100px] hover:bg-[#be8136] hover:text-white" onClick={() => finishCallConference(true, true)}>
                <FaShare size={22} />
              </ControlButton>
            ) : (
              <ControlButton name="endCall" className="bg-feedback-negative-500 text-white w-[100px] hover:bg-red-800 hover:text-white" onClick={() => finishCallConference(true)}>
                {callDetails?.transferenceStatus && callDetails.transferenceStatus.isTransferenceOrigin && participants.length > 1 ? (
                  "Concluir"
                ) : (
                  <FaPhone size={22} style={{ transform: "rotate(-135deg)" }} />
                )}
              </ControlButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
